/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Stack, Typography, TableRow, TableCell, Table, TableHead, TableBody, IconButton, TextField, Box, Button, Divider, } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';

import { useTranslation } from 'react-i18next';
import { AddCircleOutline, Delete, EventRepeat, FiberManualRecord} from '@mui/icons-material';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { IAccountGuiModel } from '../../../model/IAccountGuiModel';
import { NumericFormatWhole, RoundNumAccounting, StatusIconVoucherRegistration } from '../../../common/CustomFunctions';
import { ISourceDocumentLineGuiModel } from '../../../model/ISourceDocumentGuiModel';
import DialogVoucherRegistrationTableAccountSearch from './DialogVoucherRegistrationTableAccountSearch';
import { IVatGuiModel } from '../../../model/IVatGuiModel';
import DialogVoucherRegistrationTableVatSearch from './DialogVoucherRegistrationTableVatSearch';
import { IDepartmentGuiModel } from '../../../model/IDepartmentGuiModel';
import { IProjectGuiModel } from '../../../model/IProjectGuiModel';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import DialogVoucherRegistrationTableDepartmentSearch from './DialogVoucherRegistrationTableDepartmentSearch';
import DialogVoucherRegistrationTableProjectSearch from './DialogVoucherRegistrationTableProjectSearch';
import DialogActorAddEdit from '../../actors/components/DialogActorAddEdit';
import DialogVoucherRegistrationAmountInput from './DialogVoucherRegistrationAmountInput';
import DialogVoucherRegistrationTableCrossReferenceSearch from './DialogVoucherRegistrationTableCrossReferenceSearch';
import DialogVoucherRegistrationDialogAccrual from './DialogVoucherRegistrationDialogAccrual';
import { toast } from 'react-toastify';
import AccountBusinessLogic from '../../../businessLogic/AccountBusinessLogic';
import { useParams } from 'react-router-dom';
import VatBusinessLogic from '../../../businessLogic/VatBusinessLogic';
import DepartmentBusinessLogic from '../../../businessLogic/DepartmentBusinessLogic';
import ProjectBusinessLogic from '../../../businessLogic/ProjectBusinessLogic';
import OpenEntriesBusinessLogic from '../../../businessLogic/OpenEntriesBusinessLogic';
import { IOpenEntriesVoucherRegistratinGuiModel } from '../../../model/IOpenEntriesGuiModel';

interface IVoucherRegistrationLines {
    isSmAndUpScreen?: boolean,
    lines: ISourceDocumentLineGuiModel[],
    comment: string,
    status: string,
    handleUpdateComment: (comment: string) => void,
    handleAddLine: (copyPreviousLine?: boolean) => void,
    handleDocumentAddLines: (newLines: ISourceDocumentLineGuiModel[], index?: number) => void,
    handleUpdateLine: (lineIndex: number, newLine: ISourceDocumentLineGuiModel) => void,
    handleDeleteLine: (lineIndex: number) => void,
    banking?:boolean,
}
interface IVoucherRegistrationLine {
    index: number,
    line: ISourceDocumentLineGuiModel,
    handleAddLine: (copyPreviousLine?: boolean) => void,
    autoFocusFirstElement: boolean,
    handleUpdateLine: (index: number, line: ISourceDocumentLineGuiModel) => void,
    banking?: boolean,
    allAccountsList: IAccountGuiModel[],
    accountsListError: string[],
    accountLoader: boolean,
    vatList: IVatGuiModel[],
    vatListError: string[],
    vatLoader: boolean,
    departmentList: IDepartmentGuiModel[],
    departmentListError: string[],
    departmentLoader: boolean,
    projectList: IProjectGuiModel[],
    projectListError: string[],
    projectLoader: boolean,
    allCrossReferenceList: IOpenEntriesVoucherRegistratinGuiModel[],
    crossReferenceListError: string[],
    crossReferenceLoader: boolean,
}




const SubTable = (props: IVoucherRegistrationLine): JSX.Element => {

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const {
        index,
        line,
        handleAddLine,
        autoFocusFirstElement,
        handleUpdateLine,
        banking,
        allAccountsList,
        accountsListError,
        accountLoader,
        vatList,
        vatListError,
        vatLoader,
        departmentList,
        departmentListError,
        departmentLoader,
        projectList,
        projectListError,
        projectLoader,
        allCrossReferenceList,
        crossReferenceListError,
        crossReferenceLoader } = props;

    //type: "invoiceNo" | "debit" | "debitVat" | "credit" | "creditVat" | "amount",

    const [localText, setLocalText] = useState(line.text);
    useEffect(() => {
        setLocalText(line.text);
    }, [line.text])
    const [localInvoiceNo, setLocalInvoiceNo] = useState(line.invoiceNo);
    useEffect(() => {
        setLocalInvoiceNo(line.invoiceNo);
    }, [line.invoiceNo])
    const [localCid, setLocalCid] = useState(line.cid);
    useEffect(() => {
        setLocalCid(line.cid)
    },[line.cid])
    const handleLocalCid = (cid: string) => {
        if (/^([\d]{0,24}\-|[\d]{0,25})$/.test(cid)) {
            setLocalCid(cid);
        } else {
            toast(t("voucherRegistration.errorCidInput"));
        }
    }


    const handleUpdateInvoiceNo = (value: string) => {
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            invoiceNo: value,
        }
        handleUpdateLine(index, newLine);
    }

    const handleUpdateAmount = (amount: number | undefined, error: boolean) => {
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            amount: amount,
            amountError: error
        };
        handleUpdateLine(index, newLine);
    }
    const handleUpdateCurrencyDate = (date: Date | null, error: boolean) => {
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            currencyDate: date,
            amountError: error || !date || !line.currencyAmount || !line.currencyRate || !line.currencyVismaId 
        };
        handleUpdateLine(index, newLine);
    }
    const handleUpdateCurrencyRate = (rate: number | undefined, error: boolean) => {
        let newAmount = line.amount;
        if (rate && line.currencyAmount) {
            newAmount = RoundNumAccounting(rate * line.currencyAmount, 2);
        }
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            amount: newAmount,
            currencyRate: rate,
            amountError: error || !rate || !line.currencyAmount || !line.currencyDate || !line.currencyVismaId
        };
        handleUpdateLine(index, newLine);
    }
    const handleUpdateCurrencyAmount = (amount: number | undefined, error: boolean) => {
        let newAmount = line.amount;
        if (amount && line.currencyRate) {
            newAmount = RoundNumAccounting(amount * line.currencyRate, 2);
        }
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            amount: newAmount,
            currencyAmount: amount,
            amountError: error || !amount || !line.currencyDate || !line.currencyRate || !line.currencyVismaId,
        };
        handleUpdateLine(index, newLine);
    }
    const handleUpdateCurrencyId = (id: number | undefined, error: boolean) => {
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            currencyVismaId: id,
            amountError: error || !id || !line.currencyDate || !line.currencyAmount || !line.currencyRate
        };
        handleUpdateLine(index, newLine);
    }
    const handleUpdateCurrencyAmountDeleteAll = () => {
        handleUpdateLine(index, {
            ...line,
            amount: 0,
            amountError: false,
            currencyAmount: undefined,
            currencyRate: undefined,
            currencyDate: null,
            currencyVismaId: undefined,
        })
    }
    const handleUpdateAmountError = (status: boolean) => {
        handleUpdateLine(index, {
            ...line,
            amountError: status,
        })
    }
    const handleUpdateAccount = (type: "debet" | "credit", account: IAccountGuiModel | null, error:boolean) => {
        let newLine: ISourceDocumentLineGuiModel;
        if (type === "debet") {
            newLine = {
                ...line,
                debitAccount: account ? account : undefined,
                debitAccountError: error,
            }
        } else {
            newLine = {
                ...line,
                creditAccount: account ? account : undefined,
                creditAccountError: error,
            }
        }
        handleUpdateLine(index, newLine);
    }
    const handleUpdateVat = (type: "debetVat" | "creditVat", vat: IVatGuiModel | null, error: boolean) => {
        let newLine: ISourceDocumentLineGuiModel;
        if (type === "debetVat") {
            newLine = {
                ...line,
                debitVat: vat ? vat : undefined,
                debitVatError: error,
            }
        } else {
            newLine = {
                ...line,
                creditVat: vat ? vat : undefined,
                creditVatError: error,
            }
        }
        handleUpdateLine(index, newLine);
    }

    const handleUpdateDate = (date: Date | null, type: "voucherDate" | "dueDate" | "valueDate", error: boolean) => {
        let newLine: ISourceDocumentLineGuiModel;
        if (type === "voucherDate") {
            newLine = {
                ...line,
                voucherDate: date,
                voucherDateError: error,
            }
        } else if (type === "dueDate") {
            newLine = {
                ...line,
                dueDate: date,
                dueDateError: error,
            }
        } else {
            newLine = {
                ...line,
                valueDate: date,
                valueDateError: error,
            }
        }

        handleUpdateLine(index, newLine);
    }

    const handleUpdateDepartment = (department: IDepartmentGuiModel | null, error: boolean) => {
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            department: department ? department : undefined,
            departmentError: error,
        }
        handleUpdateLine(index, newLine);
    }
    const handleUpdateProject = (project: IProjectGuiModel | null, error: boolean) => {
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            project: project ? project : undefined,
            projectError: error,
        }
        handleUpdateLine(index, newLine);
    }

    const handleUpdateText = (value: string) => {
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            text: value,
        }
        handleUpdateLine(index, newLine);
    }
    const handleUpdateCid = (value: string) => {
        let newLine: ISourceDocumentLineGuiModel = {
            ...line,
            cid: value,
        }
        handleUpdateLine(index, newLine);
    }
    const handleUpdateCrossReference = (
        crossReferenceInvoiceNo: string | undefined,
        amount: number | null,
        currencyAmount: number | undefined,
        currencyRate: number | undefined,
        currencyDate: Date | null,
        currencyVismaId: number | undefined,

        error: boolean) => {
        
        handleUpdateLine(index, {
            ...line,
            crossReference: crossReferenceInvoiceNo,
            amount: amount ? amount : line.amount,
            crossReferenceError: error,
            amountError: false,
            currencyAmount: amount ? currencyAmount : line.currencyAmount,
            currencyRate: amount ? currencyRate : line.currencyRate,
            currencyDate: amount ? (currencyAmount || currencyRate || currencyVismaId) ? currencyDate : null : line.currencyDate,
            currencyVismaId: amount ? currencyVismaId : line.currencyVismaId,
        })
    }
    const handleInputOnEnter = () => {
        handleAddLine(true);
    }

    const cidInputRef = useRef<HTMLInputElement>(null);
    const focusOnCidInput = () => {
        //if (cidInputRef.current) {
        //    cidInputRef.current.focus();
        //}
        const cidInput: HTMLInputElement | null = document.querySelector("#cidId" + index);
        if (cidInput) {
            cidInput.focus();
            cidInput.select();
        }
    }

    const [voucherDateError, setVoucherDateError] = useState(false);
    const [dueDateError, setDueDateError] = useState(false);
    const [valueDateError, setValueDateError] = useState(false);

    return <Box display="flex" flex="1 1 auto" flexDirection="column">
        <Table stickyHeader size="small" padding="none">
            <TableHead>
                <TableRow>
                    <TableCell>{t("reports.invoiceNo")}</TableCell>
                    <TableCell>{t("search.debet")}</TableCell>
                    <TableCell>{t("search.debetVat")}</TableCell>
                    <TableCell>{t("search.credit")}</TableCell>
                    <TableCell>{t("search.creditVat")}</TableCell>
                    <TableCell>{t("search.amount")}</TableCell>
                    <TableCell>{t("search.cid")}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell sx={{ paddingRight: theme.spacing(0.5), }}>
                        <TextField
                            autoFocus={autoFocusFirstElement}
                            size="small"
                            variant="filled"
                            value={localInvoiceNo ?? ""}
                            onChange={(e) => setLocalInvoiceNo(e.target.value) }
                            onBlur={(e) => { handleUpdateInvoiceNo(e.target.value) }}
                            onKeyUp={(e) => {
                                if (e.key === "Enter") {
                                    handleAddLine();
                                }
                            }}
                            inputProps={{ maxLength: 20 }}
                            InputLabelProps={{ shrink: true, }}
                            fullWidth
                            sx={{
                                minWidth: "6rem", "& .MuiInputBase-root": { height: "2.5rem" },
                                "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper }
                            }}
                            InputProps={{ disableUnderline: true }}
                            id="invoiceNo" />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DialogVoucherRegistrationTableAccountSearch
                            account={line.debitAccount ? line.debitAccount : null}
                            handleUpdate={handleUpdateAccount}
                            handleInputOnEnter={handleInputOnEnter}
                            type="debet"
                            allAccountsList={allAccountsList}
                            accountsListError={accountsListError}
                            accountloader={accountLoader} />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DialogVoucherRegistrationTableVatSearch
                            vat={line.debitVat ? line.debitVat : null}
                            handleUpdate={handleUpdateVat}
                            handleInputOnEnter={handleInputOnEnter}
                            type="debetVat"
                            vatList={vatList}
                            vatListError={vatListError}
                            vatLoader={vatLoader}                        />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DialogVoucherRegistrationTableAccountSearch
                            account={line.creditAccount ? line.creditAccount : null}
                            handleUpdate={handleUpdateAccount}
                            handleInputOnEnter={handleInputOnEnter}
                            type="credit"
                            allAccountsList={allAccountsList}
                            accountsListError={accountsListError}
                            accountloader={accountLoader}                        />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DialogVoucherRegistrationTableVatSearch
                            vat={line.creditVat ? line.creditVat : null}
                            handleUpdate={handleUpdateVat}
                            handleInputOnEnter={handleInputOnEnter}
                            type="creditVat"
                            vatList={vatList}
                            vatListError={vatListError}
                            vatLoader={vatLoader}                        />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DialogVoucherRegistrationAmountInput
                            amountObj={{
                                amount: line.amount,
                                currency: line.currencyAmount || line.currencyDate || line.currencyRate || line.currencyVismaId ?
                                    {
                                        currencyAmount: line.currencyAmount,
                                        currencyDate: line.currencyDate,
                                        currencyRate: line.currencyRate,
                                        currencyVismaId: line.currencyVismaId
                                    } : undefined,
                                error: line.currencyAmount || line.currencyDate || line.currencyRate || line.currencyVismaId ? line.currencyAmount && line.currencyDate && line.currencyRate && line.currencyVismaId ? false : true : false
                            }}
                            handleUpdateAmount={handleUpdateAmount}
                            handleUpdateCurrencyAmount={handleUpdateCurrencyAmount}
                            handleUpdateCurrencyDate={handleUpdateCurrencyDate}
                            handleUpdateCurrencyRate={handleUpdateCurrencyRate}
                            handleUpdateCurrencyId={handleUpdateCurrencyId}
                            handleUpdateAmountError={handleUpdateAmountError}
                            handleInputOnEnter={handleInputOnEnter}
                            handleUpdateCurrencyAmountDeleteAll={handleUpdateCurrencyAmountDeleteAll}
                            focusOnCidInput={focusOnCidInput} />
                    </TableCell>
                    <TableCell sx={{ paddingRight: theme.spacing(0.5), }}>
                        <TextField
                            size="small"
                            variant="filled"
                            value={localCid ?? ""}
                            onChange={(e) => handleLocalCid(e.target.value) }
                            onBlur={(e) => { handleUpdateCid(e.target.value) }}
                            onKeyUp={(e) => {
                                if (e.key === "Enter") {
                                    handleAddLine();
                                }
                            }}
                            inputProps={{ maxLength: 25 }}
                            InputLabelProps={{ shrink: true, }}
                            fullWidth
                            sx={{
                                minWidth: "6rem", "& .MuiInputBase-root": { height: "2.5rem" },
                                "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper }
                            }}
                            InputProps={{ disableUnderline: true }}
                            id={"cidId" + index} name={"cidId" + index} />
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
        <Table stickyHeader size="small" padding="none">
            <TableHead>
                <TableRow>
                    <TableCell>{t("home.invoiceDate")}</TableCell>
                    <TableCell>{t("search.dueDate")}</TableCell>
                    <TableCell>{t("search.valueDate")}</TableCell>
                    <TableCell>{t("search.department")}</TableCell>
                    <TableCell>{t("search.project")}</TableCell>
                    <TableCell>{t("basic.text")}</TableCell>
                    <TableCell>{t("basic.crossReference")}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell sx={{ paddingRight: theme.spacing(0.5), }}>
                        <DesktopDatePicker
                        
                            disableOpenPicker
                            value={line.voucherDate ? new Date(line.voucherDate) : null}
                            onChange={(newValue, context) => {
                                handleUpdateDate(newValue, "voucherDate", context.validationError ? true : false)
                            }}
                            onError={(error, value) => {
                                setVoucherDateError(error ? true : false);                     
                            }}
                            slotProps={{
                                textField: {
                                    inputRef: cidInputRef,
                                    size: "small",
                                    variant: "filled",
                                    InputProps: { disableUnderline: true },
                                    onKeyUp: (e) => {
                                        if (e.key === "Enter") {
                                            handleAddLine();
                                        }
                                    },
                                    sx: {
                                        minWidth: "6rem",
                                        "& .MuiInputBase-root": { height: "2.5rem" },
                                        "& .MuiFilledInput-root": { backgroundColor: voucherDateError ? theme.palette.error.light : theme.palette.background.paper },
                                    },
                                    id:"voucherDateId"
                                },
                            }} />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DesktopDatePicker
                            disableOpenPicker
                            value={line.dueDate ? new Date(line.dueDate) : null}
                            onChange={(newValue, context) => handleUpdateDate(newValue, "dueDate", context.validationError ? true : false)}
                            onError={(error, value) => {
                                setDueDateError(error ? true : false);
                                
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    variant: "filled",
                                    InputProps: { disableUnderline: true },
                                    onKeyUp: (e) => {
                                        if (e.key === "Enter") {
                                            handleAddLine();
                                        }
                                    },
                                    sx: {
                                        minWidth: "6rem",
                                        "& .MuiInputBase-root": { height: "2.5rem" },
                                        "& .MuiFilledInput-root": { backgroundColor: dueDateError ? theme.palette.error.light : theme.palette.background.paper }
                                    }
                                },
                            }} />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DesktopDatePicker
                            disableOpenPicker
                            value={line.valueDate ? new Date(line.valueDate) : null}
                            onChange={(newValue, context) => handleUpdateDate(newValue, "valueDate", context.validationError ? true : false)}
                            onError={(error, value) => {
                                setValueDateError(error ? true : false);
                                
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    variant: "filled",
                                    InputProps: { disableUnderline: true },
                                    onKeyUp: (e) => {
                                        if (e.key === "Enter") {
                                            handleAddLine();
                                        }
                                    },
                                    sx: {
                                        minWidth: "6rem",
                                        "& .MuiInputBase-root": { height: "2.5rem" },
                                        "& .MuiFilledInput-root": { backgroundColor: valueDateError ? theme.palette.error.light : theme.palette.background.paper }
                                    }
                                },
                            }} />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DialogVoucherRegistrationTableDepartmentSearch
                            department={line.department ? line.department : null}
                            handleUpdate={handleUpdateDepartment}
                            handleInputOnEnter={handleInputOnEnter}
                            departmentList={departmentList}
                            departmentListError={departmentListError}
                            departmentLoader={departmentLoader} />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <DialogVoucherRegistrationTableProjectSearch
                            project={line.project ? line.project : null}
                            handleUpdate={handleUpdateProject}
                            handleInputOnEnter={handleInputOnEnter}
                            projectList={projectList}
                            projectListError={projectListError}
                            projectLoader={projectLoader} />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        <TextField
                            size="small"
                            variant="filled"
                            value={localText ?? ""}
                            onChange={(event) => setLocalText(event.target.value)}
                            onBlur={(event) => { handleUpdateText(event.target.value) }}
                            onKeyUp={(e) => {
                                if (e.key === "Enter") {
                                    handleAddLine();
                                }
                            }}
                            inputProps={{ maxLength: 200 }}
                            InputLabelProps={{ shrink: true, }}
                            fullWidth
                            sx={{
                                minWidth: "6rem", "& .MuiInputBase-root": { height: "2.5rem" },
                                "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper }
                            }}
                            InputProps={{ disableUnderline: true }}
                            id="textId" />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(0.5) }}>
                        {banking ?
                            <DialogVoucherRegistrationTableCrossReferenceSearch
                                crossReferenceInvoiceId={line.crossReference ? line.crossReference : undefined}
                                handleUpdate={handleUpdateCrossReference}
                                handleInputOnEnter={handleInputOnEnter}
                                allCrossReferenceList={allCrossReferenceList}
                                crossReferenceListError={crossReferenceListError}
                                crossReferenceLoader={crossReferenceLoader} />
                            :
                            <TextField
                                size="small"
                                variant="filled"
                                value={""}
                                onChange={undefined}
                                disabled
                                InputLabelProps={{ shrink: true, }}
                                sx={{
                                    minWidth: "6rem", "& .MuiInputBase-root": { height: "2.5rem" },
                                    "& .MuiFilledInput-root": { backgroundColor: theme.palette.grey[400] }
                                }}
                                InputProps={{ disableUnderline: true }}
                                id="notInUse" />}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </Box>

}

const DialogVoucherRegistrationLines = (props: IVoucherRegistrationLines): JSX.Element => {

    const { isSmAndUpScreen, lines, comment, status, handleAddLine, handleDocumentAddLines, handleUpdateLine, handleDeleteLine, handleUpdateComment, banking } = props;

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const { organizationId } = useParams();

    const [voucherComment, setVoucherComment] = useState(comment);

    const [allAccountsList, setAllAccountsList] = useState<IAccountGuiModel[]>([]);
    const [accountsListError, setAccountsListError] = useState<string[]>([]);
    const [accountLoader, setAccountLoader] = useState(false);

    const [vatList, setVatList] = useState<IVatGuiModel[]>([]);
    const [vatListError, setVatListError] = useState<string[]>([]);
    const [vatLoader, setVatLoader] = useState(false);

    const [departmentList, setDepartmentList] = useState<IDepartmentGuiModel[]>([]);
    const [departmentListError, setDepartmentListError] = useState<string[]>([]);
    const [departmentloader, setDepartmentLoader] = useState(false);

    const [projectList, setProjectList] = useState<IProjectGuiModel[]>([]);
    const [projectListError, setProjectListError] = useState<string[]>([]);
    const [projectLoader, setProjectLoader] = useState(false);

    const [allCrossReferenceList, setAllCrossReferenceList] = useState<IOpenEntriesVoucherRegistratinGuiModel[]>([]);
    const [crossReferenceListError, setCrossReferenceListError] = useState<string[]>([]);
    const [crossReferenceLoader, setCrossReferenceLoader] = useState(false);

    useEffect(() => {
        setVoucherComment(comment);
    }, [comment]);

    const [addActorOpen, setAddActorOpen] = useState(false);
    const handleAddActorOpen = () => {
        setAddActorOpen(true);
    }
    const handleAddActorClose = () => {
        setAddActorOpen(false);
    }


    //const [autoFocusNewLine, setAutoFocusAddLine] = useState(false);

    const handleAddLineLocal = (copyPreviousLine?: boolean) => {
        //setAutoFocusAddLine(true);
        handleAddLine(copyPreviousLine);
    }
    const handleDeleteLineLocal = (index: number) => {
        //setAutoFocusAddLine(false);
        handleDeleteLine(index);
    }

    const [dialogAccrualNumber, setDialogAccrualNumber] = useState<number | undefined>(undefined);

    const handleOpenAccrual = (index: number) => {
        if (
            //lines[0].debitAccount &&
            //!lines[0].debitAccountError &&
            //lines[0].creditAccount &&
            //!lines[0].creditAccountError &&

            //((lines[index].debitVat && !lines[index].debitVatError) || (lines[index].creditVat && !lines[index].creditVatError)) &&


            lines[index].amount &&
            !lines[index].amountError &&
            lines[index].valueDate &&
            !lines[index].valueDateError) {

            setDialogAccrualNumber(index);
        } else {
            toast(t("voucherRegistration.accrualMissingFields"));
        }
        
    }
    const handleCloseAccrual = () => {
        setDialogAccrualNumber(undefined);
    }

    const getAccounts = async (isMounted: boolean) => {

        let logic = new AccountBusinessLogic();
        setAccountLoader(true);
        const result = await logic.getAccounts(organizationId ? organizationId : "", "", undefined);
        setAccountLoader(false);
        if (result.hasErrors) {
            if (isMounted) setAccountsListError(result.errors);
        } else {
            if (isMounted) setAccountsListError([]);
            if (isMounted) {
                setAllAccountsList(result.value);
            }
        }
    }
    const getVats = async (isMounted: boolean) => {

        let logic = new VatBusinessLogic();
        setVatLoader(true);
        const result = await logic.getVatCodes(organizationId ? organizationId : "");
        setVatLoader(false);
        if (result.hasErrors) {
            if (isMounted) setVatListError(result.errors);
        } else {
            if (isMounted) setVatListError([]);
            if (isMounted) {
                setVatList(result.value);
            }
        }
    }
    const getDepartments = async (isMounted: boolean) => {

        let logic = new DepartmentBusinessLogic();
        setDepartmentLoader(true);
        const result = await logic.getDepartments(organizationId ? organizationId : "");
        setDepartmentLoader(false);
        if (result.hasErrors) {
            if (isMounted) setDepartmentListError(result.errors);
        } else {
            if (isMounted) setDepartmentListError([]);
            if (isMounted) {
                setDepartmentList(result.value);
            }
        }
    }
    const getProjects = async (isMounted: boolean) => {

        let logic = new ProjectBusinessLogic();
        setProjectLoader(true);
        const result = await logic.getProjects(organizationId ? organizationId : "");
        setProjectLoader(false);
        if (result.hasErrors) {
            if (isMounted) setProjectListError(result.errors);
        } else {
            if (isMounted) setProjectListError([]);
            if (isMounted) {
                setProjectList(result.value);
            }
        }
    }
    const getCrossReferences = async (isMounted: boolean) => {

        let logic = new OpenEntriesBusinessLogic();
        setCrossReferenceLoader(true);
        const result = await logic.getAll(organizationId ? organizationId : "");
        setCrossReferenceLoader(false);
        if (result.hasErrors) {
            if (isMounted) setCrossReferenceListError(result.errors);
        } else {
            if (isMounted) setCrossReferenceListError([]);
            if (isMounted) {

                let crossReferences: IOpenEntriesVoucherRegistratinGuiModel[] = [];
                for (let i = 0; i < result.value.length; i++) {
                    for (let j = 0; j < result.value[i].openEntries.length; j++) {
                        crossReferences.push({
                            actorId: result.value[i].actorId,
                            actorName: result.value[i].actorName,
                            actorType: result.value[i].actorType,
                            invoiceNo: result.value[i].openEntries[j].invoiceNo,
                            dueDate: result.value[i].openEntries[j].dueDate,
                            amount: result.value[i].openEntries[j].amount,
                            amountInCurrency: result.value[i].openEntries[j].amountInCurrency,
                            exchangeRate: result.value[i].openEntries[j].exchangeRate,
                            currencyNumber: result.value[i].openEntries[j].currencyNumber
                        });
                    }
                }

                setAllCrossReferenceList(crossReferences);
            }
        }
    }

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        getAccounts(isMounted);
        getVats(isMounted);
        getDepartments(isMounted);
        getProjects(isMounted);
        getCrossReferences(isMounted);

        return () => { isMounted = false };

    }, [organizationId]);

    return <Grid container direction="column"
        sx={{
            //border: 1,
            //borderColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.grey[50],
            width: "100%",
            height:"100%",
            overflowX: "auto",
        }}>
        <Grid xs={12}>
            <Stack direction="row" padding={theme.spacing(2)} spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="contained" onClick={handleAddActorOpen}><AddCircleOutline /></Button>
                    <Divider orientation="vertical" />
                    <Typography variant="caption">{"Status"}</Typography>
                    <StatusIconVoucherRegistration status={status} />
                    <Divider orientation="vertical" />
                </Stack>
                <TextField
                    size="small"
                    variant="outlined"
                    autoComplete="off"
                    color="warning"
                    value={voucherComment}
                    label={t("basic.comment")}
                    inputProps={{ maxLength: 200 }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setVoucherComment(event.target.value)}
                    onBlur={(event) => handleUpdateComment(event.target.value)}
                    fullWidth
                    id="voucherCommentId" />
                <Button disabled sx={{minWidth:"16rem"}} variant="contained" color="secondary">{t("voucherRegistration.activateFreeJournalEdit")}</Button>
            </Stack>
            {addActorOpen && <DialogActorAddEdit open={addActorOpen} closeDialog={handleAddActorClose} actorType={undefined} actor={undefined} />}
        </Grid>
        {lines.map((l, index) => {
            return <Grid xs={12} padding={theme.spacing(2)} paddingBottom={0} paddingTop={1}>
                <Stack direction="row" border={2} borderColor={theme.palette.warning.main} borderRadius={theme.shape.borderRadius}>
                    <Button
                        variant="contained"
                        onClick={() => handleOpenAccrual(index)}
                        sx={{ minWidth: "0" }}
                        size="small">
                        <EventRepeat />
                    </Button>
                    <SubTable
                        index={index}
                        line={l}
                        handleAddLine={handleAddLineLocal}
                        autoFocusFirstElement={index === (lines.length - 1) ? true : false}
                        handleUpdateLine={handleUpdateLine} banking={banking}
                        allAccountsList={allAccountsList}
                        accountsListError={accountsListError}
                        accountLoader={accountLoader}
                        vatList={vatList}
                        vatListError={vatListError}
                        vatLoader={vatLoader}
                        departmentList={departmentList}
                        departmentListError={departmentListError}
                        departmentLoader={departmentloader}
                        projectList={projectList}
                        projectListError={projectListError}
                        projectLoader={projectLoader}
                        allCrossReferenceList={allCrossReferenceList}
                        crossReferenceListError={crossReferenceListError}
                        crossReferenceLoader={crossReferenceLoader} />
                    <Button
                        variant="contained"
                        onClick={() => handleDeleteLineLocal(index)}
                        sx={{ minWidth: "0" }}
                        size="small"
                        color="error">
                        <Delete />
                    </Button>
                </Stack>
            </Grid>
        })}
        <Grid xs={12} display="flex" justifyContent="center"><IconButton onClick={() => handleAddLine(true)}><FiberManualRecord sx={{ fontSize: "1rem" }} /></IconButton></Grid>
        <Grid xs={12} display="flex" justifyContent="center"><IconButton onClick={() => handleAddLine(true)}><FiberManualRecord sx={{ fontSize: "1rem" }} /></IconButton></Grid>
        <Grid xs={12} display="flex" justifyContent="center"><IconButton onClick={() => handleAddLine(true)}><AddCircleOutline sx={{ fontSize: "3rem" }} /></IconButton></Grid>
        {Boolean(dialogAccrualNumber) || dialogAccrualNumber === 0 ? < DialogVoucherRegistrationDialogAccrual
            line={lines[dialogAccrualNumber ? dialogAccrualNumber : 0]}
            index={dialogAccrualNumber ? dialogAccrualNumber : 0}
            handleDocumentAddLines={handleDocumentAddLines}
            open={Boolean(dialogAccrualNumber) || dialogAccrualNumber === 0 ? true : false}
            closeDialog={handleCloseAccrual} /> : <></>}
    </Grid>
}

export default DialogVoucherRegistrationLines;